import { addDays, daysBetween, mondayOf } from './days.js';
import type { LadderRung } from './ladder.js';
import type { IListItem } from './listItem.js';
import type { IWindowBounds } from './planWindow.js';
import { openSpan, rungOf } from './promise.js';

// The calendar steps its open day a day at a time.
export { addDays };

// ---------------------------------------------------------------------------
// The mobile Work Plan calendar (docs/specs/2026-10-05-mobile-work-plan-calendar-design.md):
// a month of Monday-first weeks, each promise a bar across the days it holds —
// at most a few lanes per week, the rest counted per day — and a day's items
// grouped by what a foreman does with them. Pure: the screen only renders it.
// Days are `YYYY-MM-DD` site days; a month is its first day (`YYYY-MM-01`).
// ---------------------------------------------------------------------------

/** The month a day falls in, as its first day. */
export const monthOf = (day: string): string => `${day.slice(0, 7)}-01`;

/** `month` moved by `by` months (back when negative). */
export function shiftMonth(month: string, by: number): string {
  const index = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + by;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}-01`;
}

/** The Mondays of the weeks a month's grid shows — every week holding one of its days. */
export function monthWeeks(month: string): string[] {
  const last = addDays(shiftMonth(month, 1), -1);
  const weeks: string[] = [];
  for (let monday = mondayOf(month); monday <= last; monday = addDays(monday, 7)) weeks.push(monday);
  return weeks;
}

/** The days a month's grid spans: its first week's Monday to its last week's Sunday. */
export function monthBounds(month: string): IWindowBounds {
  const weeks = monthWeeks(month);
  return { from: weeks[0]!, to: addDays(weeks.at(-1)!, 6) };
}

/** How many bar lanes a week shows before counting the rest. */
export const CALENDAR_LANES = 5;

/** One promise's bar in one week. Columns run 0 (Monday) to 6 (Sunday). */
export interface ICalendarBar {
  item: IListItem;
  rung: LadderRung;
  from: number;
  to: number;
  lane: number;
  /** The promise runs on from the week before / into the week after. */
  continuesBefore: boolean;
  continuesAfter: boolean;
  /** An overdue promise's first column past its planned end, when this week
   *  holds that overrun; null otherwise. */
  overrunFrom: number | null;
}

export interface ICalendarWeek {
  monday: string;
  bars: ICalendarBar[];
  /** Per column, the promises that hold the day but found no lane. */
  hidden: number[];
}

const LATE_RUNGS: readonly LadderRung[] = ['overdue', 'delayed'];

/** Who wins a lane: late work first, then the critical path, then the
 *  earlier start, then the longer bar — what is cut to "+N" is the least urgent. */
function byUrgency(a: ICalendarBar, b: ICalendarBar): number {
  return (
    Number(LATE_RUNGS.includes(b.rung)) - Number(LATE_RUNGS.includes(a.rung)) ||
    Number(b.item.isOnCriticalPath) - Number(a.item.isOnCriticalPath) ||
    a.item.raw.activity!.start.localeCompare(b.item.raw.activity!.start) ||
    b.to - b.from - (a.to - a.from)
  );
}

/** A week's bars, every one in its lane — `weekAt` cuts it at any number of lanes. */
export interface IPackedWeek {
  monday: string;
  bars: ICalendarBar[];
}

/**
 * One week of the grid, packed: each committed activity holding a day of it
 * becomes a bar — an overdue one runs on to today (the Work Plan's decision 5) —
 * in the first free lane by urgency. Packed once, it serves every lane count
 * (`weekAt`): a bar's lane doesn't depend on how many lanes show.
 */
export function packWeek(activities: readonly IListItem[], monday: string, today: string): IPackedWeek {
  const sunday = addDays(monday, 6);
  const bars: ICalendarBar[] = [];
  for (const item of activities) {
    const task = item.raw.activity!;
    const rung = rungOf(item);
    const [start, end] = openSpan(task, rung, today);
    if (start > sunday || end < monday) continue;
    const from = Math.max(0, daysBetween(monday, start));
    const to = Math.min(6, daysBetween(monday, end));
    const overrun = daysBetween(monday, addDays(task.end, 1));
    bars.push({
      item,
      rung,
      from,
      to,
      lane: 0,
      continuesBefore: start < monday,
      continuesAfter: end > sunday,
      overrunFrom: end > task.end && overrun <= to ? Math.max(from, overrun) : null,
    });
  }
  bars.sort(byUrgency);
  const taken: ICalendarBar[][] = [];
  for (const bar of bars) {
    const free = taken.findIndex((lane) => lane.every((other) => bar.to < other.from || bar.from > other.to));
    bar.lane = free < 0 ? taken.length : free;
    (taken[bar.lane] ??= []).push(bar);
  }
  return { monday, bars };
}

/** A packed week as it shows in `lanes` lanes: the bars that fit, and per column the rest only counted. */
export function weekAt(packed: IPackedWeek, lanes: number): ICalendarWeek {
  const hidden = Array.from({ length: 7 }, (_, column) =>
    packed.bars.filter((bar) => bar.lane >= lanes && bar.from <= column && column <= bar.to).length,
  );
  return { monday: packed.monday, bars: packed.bars.filter((bar) => bar.lane < lanes), hidden };
}

/** One week of the grid in `lanes` lanes (`packWeek`, `weekAt`). */
export function calendarWeek(
  activities: readonly IListItem[],
  monday: string,
  today: string,
  lanes: number = CALENDAR_LANES,
): ICalendarWeek {
  return weekAt(packWeek(activities, monday, today), lanes);
}

/** An issue's due day (`issueToListItem` keeps it as `end`); undefined when it has none. */
const dueOf = (issue: IListItem): string | undefined => issue.end;

/**
 * The issues a day marks: those due on it, and — on today — every issue
 * already past due, which collects there rather than on the day it slipped.
 */
export function dayIssues(issues: readonly IListItem[], day: string, today: string): { due: IListItem[]; overdue: IListItem[] } {
  return {
    due: issues.filter((issue) => dueOf(issue) === day),
    overdue: day === today ? issues.filter((issue) => issue.isOverdue) : [],
  };
}

/** A month's issue marks, counted once: those due per day, and those past due (marked on today). */
export interface IIssueMarks {
  due: ReadonlyMap<string, number>;
  overdue: number;
}

export function issueMarks(issues: readonly IListItem[]): IIssueMarks {
  const due = new Map<string, number>();
  let overdue = 0;
  for (const issue of issues) {
    const day = dueOf(issue);
    if (day) due.set(day, (due.get(day) ?? 0) + 1);
    if (issue.isOverdue) overdue += 1;
  }
  return { due, overdue };
}

/** What a day's badge shows (`dayIssues`, counted): how many issues it marks, and whether any is late. */
export function dayMark(marks: IIssueMarks, day: string, today: string): { count: number; late: boolean } {
  const late = day === today ? marks.overdue : 0;
  return { count: (marks.due.get(day) ?? 0) + late, late: late > 0 };
}

export type DayGroupId = 'overdue' | 'starting' | 'finishing' | 'running' | 'issues';

export interface IDayGroup {
  id: DayGroupId;
  items: IListItem[];
}

/**
 * A day's items in the order a foreman works them: what is overdue (a promise
 * past its planned end, and on today the issues past due), what starts, what
 * finishes, what runs through it, and the issues due on it. Empty groups are dropped.
 */
export function dayGroups(items: readonly IListItem[], day: string, today: string): IDayGroup[] {
  const activities = items.filter((item) => item.kind === 'activity');
  const issues = dayIssues(items.filter((item) => item.kind === 'issue'), day, today);
  const holding = activities.filter((item) => {
    const [start, end] = openSpan(item.raw.activity!, rungOf(item), today);
    return start <= day && day <= end;
  });
  const overrun = holding.filter((item) => day > item.raw.activity!.end);
  const onPlan = holding.filter((item) => !overrun.includes(item));
  const groups: IDayGroup[] = [
    { id: 'overdue', items: [...overrun, ...issues.overdue] },
    { id: 'starting', items: onPlan.filter((item) => item.raw.activity!.start === day) },
    { id: 'finishing', items: onPlan.filter((item) => item.raw.activity!.end === day && item.raw.activity!.start !== day) },
    { id: 'running', items: onPlan.filter((item) => item.raw.activity!.start < day && day < item.raw.activity!.end) },
    { id: 'issues', items: issues.due },
  ];
  return groups.filter((group) => group.items.length > 0);
}
